// Translates a resolve() cast into a short in-world sentence with none of the
// underlying reasoning -- the "narrative" counterpart to the raw debug output in
// ui.ts's formatResultText/Html. Branches on the same CastInput fields resolve()
// itself branches on (concept/operator/secondConcept/modifiers), so it stays a
// re-wording of what resolve() already decided, not a new hardcoded combination
// table with its own independent effects.
import { CastInput } from '../../rune-lab/src/runes/resolve';

function has(mods: string[], id: string): boolean {
  return mods.includes(id);
}

// A cast's target might be the caster themself (tapping the player) or an external
// object -- the same clause needs "you grow warm" vs. "the crate grows warm", not
// just a different noun but a different verb form, so every place a subject/verb/
// possessive is needed goes through one of these three forms instead of splicing
// the target's display name directly into a fixed-conjugation template.
interface Actor {
  subject: string; // "you" | "the old crate"
  possessive: string; // "your" | "the old crate's"
  object: string; // "yourself" | "the old crate"
  isYou: boolean;
}

function actorFor(targetName: string, isSelf: boolean, isCaster: boolean): Actor {
  if (isSelf || isCaster) return { subject: 'you', possessive: 'your', object: 'yourself', isYou: true };
  const label = `the ${targetName.toLowerCase()}`;
  return { subject: label, possessive: `${label}'s`, object: label, isYou: false };
}

// "You" takes the bare/plural verb form ("you grow"), everything else takes the
// third-person singular ("the crate grows") -- pick per-clause based on which
// actor is actually in the subject position.
function v(actor: Actor, thirdPerson: string, secondPerson: string): string {
  return actor.isYou ? secondPerson : thirdPerson;
}

function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function narrateCast(input: CastInput, targetName: string): string {
  const isSelf = targetName === 'Yourself';
  const conceptId = input.conceptId;
  const mods = input.modifierIds;
  const clauses: string[] = [];

  switch (input.operatorId) {
    case 'transfer': {
      const targetToSelf = has(mods, 'reverse');
      const to = actorFor(targetName, isSelf, targetToSelf);
      const from = actorFor(targetName, isSelf, !targetToSelf);

      if (conceptId === 'entropy') {
        // Reverse now does double duty here: direction and sign used to be
        // independent (four reachable combinations), but both read the same
        // modifier post-merge, so only the two "aligned" pairings remain --
        // see 07_Open_Questions.md.
        if (targetToSelf) {
          clauses.push(`${to.subject} ${v(to, 'looks', 'look')} cleaner and more whole, as if repaired`);
          if (!isSelf) clauses.push(`${from.subject} ${v(from, 'feels', 'feel')} a wave of decay in exchange`);
        } else {
          clauses.push(`${to.subject} suddenly ${v(to, 'shows', 'show')} rust, cracks, or rot`);
          if (!isSelf) clauses.push(`${from.subject} ${v(from, 'looks', 'look')} fresher for it`);
        }
      } else if (conceptId === 'energy') {
        clauses.push(`${to.subject} ${v(to, 'grows', 'grow')} warm to the touch`);
        if (!isSelf) clauses.push(`${from.subject} ${v(from, 'feels', 'feel')} a chill`);
      } else if (conceptId === 'momentum') {
        if (has(mods, 'oscillating')) {
          clauses.push(`${to.subject} ${v(to, 'shakes', 'shake')} and rattles in place`);
        } else {
          clauses.push(`${to.subject} ${v(to, 'is', 'are')} shoved away`);
          if (!isSelf) clauses.push(`${from.subject} ${v(from, 'staggers', 'stagger')} from the recoil`);
        }
      } else if (conceptId === 'charge') {
        if (has(mods, 'oscillating')) {
          clauses.push(`${to.subject} ${v(to, 'hums and crackles', 'hum and crackle')} rhythmically`);
        } else {
          clauses.push(`${to.subject} ${v(to, 'crackles', 'crackle')} with static`);
          if (!isSelf) clauses.push(`${from.subject} ${v(from, 'feels', 'feel')} the charge drain away`);
        }
      } else if (conceptId === 'mass') {
        clauses.push(`${to.subject} ${v(to, 'grows', 'grow')} noticeably heavier`);
        if (!isSelf) clauses.push(`${from.subject} ${v(from, 'feels', 'feel')} lighter`);
      } else if (conceptId === 'space') {
        clauses.push(
          isSelf
            ? "Nothing changes -- you're already exactly where you are"
            : `${to.subject} and ${from.subject} swap places in a blink`,
        );
      } else if (conceptId === 'time') {
        clauses.push(`${to.possessive} movements blur, faster or slower than they should be`);
      }
      break;
    }

    case 'convert': {
      const target = actorFor(targetName, isSelf, false);
      const second = input.secondConceptId;
      if (!second) {
        clauses.push('Nothing happens -- the cast fizzles with no destination in mind');
        break;
      }
      const isMassEnergyPair = (conceptId === 'mass' && second === 'energy') || (conceptId === 'energy' && second === 'mass');
      const isMomentumEntropyPair = (conceptId === 'momentum' && second === 'entropy') || (conceptId === 'entropy' && second === 'momentum');
      const isEnergyMomentumPair = (conceptId === 'energy' && second === 'momentum') || (conceptId === 'momentum' && second === 'energy');

      if (isMassEnergyPair) {
        clauses.push(
          conceptId === 'mass'
            ? `${target.subject} ${v(target, 'is', 'are')} annihilated in a blinding flash of heat and light`
            : `${target.subject} ${v(target, 'grows', 'grow')} a faint new solid mass, seemingly from nowhere`,
        );
      } else if (isMomentumEntropyPair) {
        clauses.push(`${target.subject} ${v(target, 'grinds', 'grind')} to a halt, hot to the touch`);
      } else if (isEnergyMomentumPair) {
        if (has(mods, 'oscillating')) {
          clauses.push(`${target.subject} ${v(target, 'rocks', 'rock')} back and forth, trading heat for motion and back again`);
        } else if (conceptId === 'energy') {
          clauses.push(`${target.subject} ${v(target, 'lurches', 'lurch')} forward suddenly`);
        } else {
          clauses.push(`${target.subject} ${v(target, 'shudders', 'shudder')} to a stop, warm now`);
        }
      } else {
        const compositional = [
          ['mass', 'momentum'],
          ['mass', 'space'],
          ['momentum', 'space'],
        ].some(([a, b]) => (conceptId === a && second === b) || (conceptId === b && second === a));

        if (compositional) {
          clauses.push(`Nothing happens -- ${target.object} ${v(target, 'seems', 'seem')} entirely unaffected`);
        } else {
          const { scale } = magnitudeScaleFromMods(mods);
          clauses.push(
            scale === 1 ? 'Nothing happens' : `Nothing happens to ${target.object} -- but you feel your own strength drain away`,
          );
        }
      }
      break;
    }

    case 'bind':
    case 'separate': {
      const target = actorFor(targetName, isSelf, false);
      const isBind = input.operatorId === 'bind';

      if (conceptId === 'mass') {
        const reversed = has(mods, 'reverse');
        clauses.push(
          isBind
            ? `${target.subject} ${v(target, 'fuses', 'fuse')} solid with whatever it touched`
            : reversed
              ? `${target.subject} ${v(target, 'rejoins', 'rejoin')} seamlessly with its neighbor`
              : `${target.subject} ${v(target, 'splits', 'split')} cleanly in two`,
        );
      } else if (conceptId === 'charge') {
        clauses.push(
          isBind
            ? `The crackle around ${target.object} fades to nothing`
            : `Sparks arc across ${target.object} as ${target.subject} ${v(target, 'splits', 'split')} into charged halves`,
        );
      } else if (conceptId === 'energy') {
        clauses.push(
          isBind
            ? `${target.subject} ${v(target, 'settles', 'settle')} into thermal contact with you`
            : `${target.subject} ${v(target, 'cools', 'cool')} back to ${target.possessive} own warmth, independent again`,
        );
      } else if (conceptId === 'space' && isBind) {
        clauses.push(`An invisible tether now links ${target.object} to ${target.possessive} surroundings`);
      } else if (conceptId === 'time' && isBind) {
        clauses.push(`${target.possessive} rhythm falls into sync with ${target.possessive} surroundings`);
      } else {
        const { scale } = magnitudeScaleFromMods(mods);
        clauses.push(
          scale === 1 ? 'Nothing happens' : `Nothing happens to ${target.object} -- but you feel your own strength drain away`,
        );
      }
      break;
    }

    case 'measure': {
      const target = actorFor(targetName, isSelf, false);
      if (conceptId === 'energy') clauses.push(`You sense ${target.possessive} warmth`);
      else if (conceptId === 'mass') clauses.push(`You sense ${target.possessive} heft, even through walls`);
      else if (conceptId === 'charge') clauses.push(`You feel a faint static tingle around ${target.object}`);
      else if (conceptId === 'momentum') clauses.push(isSelf ? "You sense your own motion, and how fast" : `You sense ${target.object} moving, and how fast`);
      else if (conceptId === 'space') clauses.push(isSelf ? 'You gauge exactly where you stand' : `You gauge the distance to ${target.object}`);
      else if (conceptId === 'time') clauses.push(`Something about ${target.possessive} rhythm feels faintly off`);
      else if (conceptId === 'entropy') clauses.push(`You sense whether ${target.subject} ${v(target, 'is', 'are')} decaying, or holding together against it`);
      break;
    }

    case 'project': {
      const radial = has(mods, 'radial');
      const oscillating = has(mods, 'oscillating');

      if (conceptId === 'space') {
        clauses.push(radial ? 'The space around you seems to stretch and loosen' : 'A strange shortcut opens up in a line ahead');
      } else if (conceptId === 'time') {
        clauses.push(radial ? 'Time itself seems to blur in the area' : 'A corridor of altered time stretches out ahead');
      } else if (conceptId === 'mass') {
        clauses.push(
          oscillating
            ? 'Ripples of gravity radiate outward, too subtle to see'
            : radial
              ? 'An invisible pull radiates outward'
              : 'Gravity bends sharply along a line',
        );
      } else if (conceptId === 'energy') {
        if (has(mods, 'reverse')) {
          clauses.push(radial ? 'A wave of cold spreads outward' : 'A chilling ray lances out');
        } else {
          clauses.push(radial ? 'Heat radiates outward like a bonfire' : 'A focused ray of heat lances out');
        }
      } else if (conceptId === 'charge') {
        clauses.push(
          oscillating ? 'Light spills outward' : radial ? 'Static charge radiates outward' : 'A crackling bolt of charge streaks out',
        );
      } else if (conceptId === 'momentum') {
        clauses.push(radial ? 'A shockwave knocks everything back' : 'A gust blasts forward');
      } else if (conceptId === 'entropy') {
        if (has(mods, 'reverse')) {
          clauses.push(radial ? 'An unnatural stillness spreads, as if decay itself paused' : 'A focused wave of preservation reaches out');
        } else {
          clauses.push(radial ? 'Decay spreads outward' : 'A corroding ray reaches out');
        }
      }
      break;
    }
  }

  if (!clauses.length) clauses.push('Nothing happens');
  return clauses.map(sentence).join('. ') + '.';
}

function magnitudeScaleFromMods(mods: string[]): { scale: number } {
  if (has(mods, 'unbound')) return { scale: 100 };
  if (has(mods, 'precise')) return { scale: 1 };
  return { scale: 10 };
}
